/**
 * free-treat.js
 * 
 * Written by Senha Ltd for Shine, 2026.
 *
 * Keeps exactly one free treat in the cart while the shopper has entered a
 * qualifying discount code (or via a promotional link) AND spent at least the threshold.
 * Removes it otherwise.
 *
 * Management of pricing and discounts is not performed in this script.
 * The script only manages the presence of the treat in the cart and provides
 * a progress message. Shopify's discounts make the treat $0 and enforce
 * eligibility at checkout. 
 */

(() => {
  'use strict';

  // Guard against the script being included twice (e.g. theme + app embed),
  // which would patch fetch twice and run two reconcilers.
  if (window.__freeTreatLoaded) return;
  window.__freeTreatLoaded = true;

  // ---- Configuration -------------------------------------------------------
  const CONFIG = {
    treatVariantId: 39367800651938,        // variant ID of the treat (numeric, not a GID)
    codes: ['MoreToLove', 'ShineAgain'],   // codes that unlock the treat (case-insensitive)
                                           // GoodToSeeYou is 10% only, so it's not listed
    thresholdBase: 50,                     // minimum spend in the store's base currency (USD)

    // Lines that must NOT count towards the threshold. Keep this in step with the
    // products tagged `wbf-exclude` (which the ShineAgain function excludes): the
    // cart API can't see product tags, so list their variant IDs here.
    // The pattern is a fallback matched against product title, type and vendor.
    excludeFromThreshold: {
      variantIds: [],                      // e.g. [44556677889900, 44556677889911]
      pattern: /insurance|protection|carbon offset/i,
    },

    // Hidden companion codes. A code belongs to one discount only, so ShineAgain's
    // "10% + free treat" uses two: the email applies ShineAgain (10%, a product
    // discount); the script adds ShineAgainTreat (a fixed order discount worth the
    // treat) while the shopper qualifies, and removes it when they don't.
    // An ORDER discount is used for the treat because Shopify won't combine a
    // Buy X Get Y discount with another product discount on the same items.
    companionCodes: {
      ShineAgain: 'ShineAgainTreat',
    },

    markerProperty: '_free_treat',         // private line property marking OUR treat line
    attributes: {                          // cart attributes the script uses
      code: '_treat_code',                 //   code captured from the email link
      added: '_treat_added',               //   '1' once the script has added the treat
      declined: '_treat_declined',         //   '1' if the shopper removed it themselves
    },

    debounceMs: 300,                       // wait for bursts of cart changes to settle
    requestTimeoutMs: 8000,                // give up on a cart request after this
    maxWritesPerMinute: 10,                // circuit breaker against add/remove loops
    debug: false,                          // true = log decisions to the console

    // Progress message, shown ONLY to shoppers who have the code.
    // First matching selector wins. Put the CART PAGE first: many themes keep a
    // hidden drawer on every page, including /cart.
    // Each target is a selector, or { selector, position } where position is
    // 'before' | 'after' | 'prepend' (default) | 'append'. 'before' places the
    // panel as a sibling, outside containers that other apps inject into.
    messageTargets: [
      { selector: 'form[action$="/cart"] table', position: 'before' },  // Shine: above the product table
      '#main-cart-footer',                 // Dawn-family cart page
      'cart-drawer .drawer__footer',       // Dawn-family cart drawer
      { selector: 'form[action$="/cart"]', position: 'prepend' },        // generic fallback
    ],
    messages: {
      remaining: 'Spend {amount} more for a free treat',
      unlocked: 'Your free treat is in your basket',
      declined: 'You’ve unlocked a free treat.',
      // Treat is in the basket but not yet priced at $0 (e.g. a guest whose
      // segment Shopify can only confirm at checkout). Never claim it's free yet.
      pending: 'Your treat is in your basket. It will show as free at checkout once your offer is confirmed.',
      addBack: 'Add it to my basket',
      progressLabel: 'Progress towards your free treat',
    },
  };

  const A = CONFIG.attributes;
  const log = (...args) => CONFIG.debug && console.info('[free-treat]', ...args);
  const warn = (...args) => console.warn('[free-treat]', ...args);
  const norm = (code) => String(code).trim().toUpperCase();

  // ---- Validate configuration: fail loudly and do nothing if it's wrong ----
  const problems = [];
  if (!Number.isInteger(CONFIG.treatVariantId) || CONFIG.treatVariantId <= 0) {
    problems.push('treatVariantId must be a positive whole number');
  } else if (CONFIG.treatVariantId === 123456789) {
    problems.push('treatVariantId is still the placeholder');
  }
  if (!Array.isArray(CONFIG.codes) || CONFIG.codes.length === 0) {
    problems.push('codes must list at least one code');
  }
  if (!(Number(CONFIG.thresholdBase) > 0)) {
    problems.push('thresholdBase must be greater than 0');
  }
  if (problems.length) {
    console.error('[free-treat] disabled:', problems.join('; '));
    return;
  }

  const TREAT_CODES = new Set(CONFIG.codes.map(norm));
  const COMPANIONS = new Map(
    Object.entries(CONFIG.companionCodes || {}).map(([primary, companion]) => [norm(primary), String(companion)])
  );
  const COMPANION_CODES = new Set([...COMPANIONS.values()].map(norm));
  const CODE_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;   // what we accept from URLs / applyCode

  // ---- Plumbing ------------------------------------------------------------
  const root = window.Shopify?.routes?.root || '/';   // locale-aware URLs
  const originalFetch = window.fetch.bind(window);    // unpatched, for our own calls
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  let syncing = false;           // a reconcile is running
  let pending = false;           // a change arrived mid-run: run again afterwards
  let timer = null;              // debounce timer
  let treatUnavailable = false;  // stop retrying if the treat is sold out
  let lastState = null;          // latest eligibility picture, used to draw the message
  let lastDiagnostics = null;    // how the last calculation was made (FreeTreat.diagnose())

  /**
   * One cart API call with a timeout, a check that the reply is JSON, and
   * retries for rate limits / server errors. add.js is only retried on 429
   * (Shopify didn't process it), because retrying after a timeout could add twice.
   */
  async function cartRequest(path, body, attempt = 0) {
    const retryable = !body || path !== 'cart/add.js';
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), CONFIG.requestTimeoutMs);

    let res;
    try {
      res = await originalFetch(root + path, {
        method: body ? 'POST' : 'GET',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
        signal: controller.signal,
      });
    } catch (err) {
      if (retryable && attempt < 2) {
        await sleep(500 * 2 ** attempt);
        return cartRequest(path, body, attempt + 1);
      }
      throw new Error(err.name === 'AbortError' ? `Timed out: ${path}` : `Network error: ${path}`);
    } finally {
      clearTimeout(timeout);
    }

    if ((res.status === 429 || (retryable && res.status >= 500)) && attempt < 2) {
      const retryAfter = Number(res.headers.get('Retry-After'));
      await sleep(retryAfter > 0 ? Math.min(retryAfter, 10) * 1000 : 500 * 2 ** attempt);
      return cartRequest(path, body, attempt + 1);
    }

    // Don't trust the Content-Type label: Shopify serves cart.js with different
    // types on different stores (e.g. text/javascript). Check the body itself.
    const text = await res.text().catch(() => '');
    let data = null;
    try {
      data = text ? JSON.parse(text) : null;
    } catch {
      data = null;
    }
    const isObject = data !== null && typeof data === 'object';

    if (!res.ok || !isObject) {
      const reason = !res.ok
        ? data?.description || data?.message || `HTTP ${res.status}`
        : `response was not JSON (${res.headers.get('content-type') || 'no content type'})`;
      const err = new Error(`Cart error on ${path}: ${reason}`);
      err.status = res.status;
      throw err;
    }
    return data;
  }

  // Circuit breaker: if another app or theme script keeps undoing our changes,
  // we'd add/remove forever. Cap writes per minute and stop if it's exceeded.
  const writeTimes = [];
  let breakerWarned = false;
  async function write(path, body) {
    const now = Date.now();
    while (writeTimes.length && now - writeTimes[0] > 60000) writeTimes.shift();
    if (writeTimes.length >= CONFIG.maxWritesPerMinute) {
      if (!breakerWarned) warn('Too many cart changes in a minute; pausing to avoid a loop.');
      breakerWarned = true;
      throw new Error('Circuit breaker open');
    }
    writeTimes.push(now);
    return cartRequest(path, body);
  }

  // ---- Reading the cart ----------------------------------------------------

  /** Upper-cased set of discount codes the shopper has entered. */
  function enteredCodes(cart) {
    const codes = new Set();
    const add = (c) => c && codes.add(norm(c));

    // Best signal: if this store's cart.js includes discount_codes, it lists every
    // entered code, including ones not yet applicable. Trust it exclusively.
    if (Array.isArray(cart.discount_codes)) {
      cart.discount_codes.forEach((d) => add(d?.code));
      return codes;
    }

    // Otherwise: codes that are currently applying show up as discount applications.
    (cart.cart_level_discount_applications || []).forEach((d) => {
      if (d?.type === 'discount_code') add(d.title);
    });
    cart.items.forEach((item) =>
      (item.line_level_discount_allocations || []).forEach((a) => {
        if (a?.discount_application?.type === 'discount_code') add(a.discount_application.title);
      })
    );

    // Last resort: the code captured from the email link or applyCode().
    add(cart.attributes?.[A.code]);
    return codes;
  }

  /** Threshold converted to the shopper's presentment currency, in cents. */
  function thresholdInCents() {
    const rate = Number(window.Shopify?.currency?.rate);
    const safeRate = Number.isFinite(rate) && rate > 0 ? rate : 1;
    return Math.round(CONFIG.thresholdBase * safeRate * 100);
  }

  const EXCLUDED_VARIANTS = new Set((CONFIG.excludeFromThreshold?.variantIds || []).map(Number));
  const EXCLUDE_PATTERN = CONFIG.excludeFromThreshold?.pattern;
  const isExcluded = (item) =>
    EXCLUDED_VARIANTS.has(Number(item.variant_id)) ||
    (EXCLUDE_PATTERN instanceof RegExp &&
      EXCLUDE_PATTERN.test([item.product_title, item.product_type, item.vendor].join(' ')));

  // Both checks: the marker alone could be put on any product by a crafted request.
  const isTreatLine = (item) =>
    Number(item.variant_id) === CONFIG.treatVariantId &&
    Boolean(item.properties?.[CONFIG.markerProperty]);

  // ---- Companion codes -----------------------------------------------------

  /** Codes on the cart, original spelling, de-duplicated (best effort). */
  function codesOnCart(cart) {
    const seen = new Map();
    const add = (c) => { if (c && !seen.has(norm(c))) seen.set(norm(c), String(c).trim()); };
    if (Array.isArray(cart.discount_codes)) {
      cart.discount_codes.forEach((d) => add(d?.code));
    } else {
      // Older cart.js: only codes that are applying are visible, plus the one
      // captured from the email link.
      (cart.cart_level_discount_applications || []).forEach((d) => {
        if (d?.type === 'discount_code') add(d.title);
      });
      cart.items.forEach((item) =>
        (item.line_level_discount_allocations || []).forEach((a) => {
          if (a?.discount_application?.type === 'discount_code') add(a.discount_application.title);
        })
      );
      add(cart.attributes?.[A.code]);
    }
    return [...seen.values()];
  }

  // If Shopify keeps refusing a companion code (e.g. misspelt, inactive), stop
  // re-adding it after a couple of tries instead of looping.
  const companionTries = new Map();

  /**
   * Add or remove companion codes so they're present exactly while the shopper
   * qualifies for the treat. Returns true if the cart's codes were changed.
   */
  /** Is a companion code currently applying (so the treat is effectively free)? */
  function companionCovers(cart) {
    if (COMPANION_CODES.size === 0) return false;
    const applied = (cart.cart_level_discount_applications || []).some(
      (d) => COMPANION_CODES.has(norm(d?.title)) && Number(d?.total_allocated_amount) > 0
    );
    const flagged = Array.isArray(cart.discount_codes) && cart.discount_codes.some(
      (d) => COMPANION_CODES.has(norm(d?.code)) && d.applicable === true
    );
    return applied || flagged;
  }

  // Warn (once per code pair) if a primary code stops applying once its companion
  // is on the cart: almost always a combination-settings problem in the admin.
  const combinationWarned = new Set();
  function checkCombination(cart) {
    if (!Array.isArray(cart.discount_codes)) return;
    const byCode = new Map(cart.discount_codes.map((d) => [norm(d?.code), d]));
    for (const [primary, companion] of COMPANIONS) {
      const p = byCode.get(primary);
      const c = byCode.get(norm(companion));
      const key = `${primary}|${norm(companion)}`;
      if (p && c && c.applicable && p.applicable === false && !combinationWarned.has(key)) {
        combinationWarned.add(key);
        warn(`${p.code} stopped applying when ${companion} was added. In Discounts, ` +
          `allow both codes to combine with product discounts (and same item, tag WBF).`);
      }
    }
  }

  async function syncCompanions(cart, wantTreat) {
    if (COMPANIONS.size === 0) return false;
    checkCombination(cart);
    const current = codesOnCart(cart);
    const present = new Set(current.map(norm));

    const wanted = new Set();
    if (wantTreat) {
      for (const [primary, companion] of COMPANIONS) {
        if (present.has(primary)) wanted.add(norm(companion));
      }
    }

    const next = current.filter((c) => !COMPANION_CODES.has(norm(c)) || wanted.has(norm(c)));
    for (const companion of COMPANIONS.values()) {
      if (!wanted.has(norm(companion)) || present.has(norm(companion))) continue;
      const tries = companionTries.get(norm(companion)) || 0;
      if (tries >= 2) {
        if (tries === 2) warn(`Shopify won't keep the code ${companion} on the cart; check it exists, is active and combines.`);
        companionTries.set(norm(companion), tries + 1);
        continue;
      }
      companionTries.set(norm(companion), tries + 1);
      next.push(companion);
    }

    const same = next.length === current.length && next.every((c, i) => norm(c) === norm(current[i]));
    if (same) return false;
    log('codes', current, '→', next);
    await write('cart/update.js', { discount: next.join(',') });
    return true;
  }

  // ---- The reconciler ------------------------------------------------------

  async function reconcile() {
    const cart = await cartRequest('cart.js');
    if (!cart || !Array.isArray(cart.items)) throw new Error('Unexpected cart response');

    const attrs = cart.attributes || {};
    const treatLines = cart.items.filter(isTreatLine);
    // Only real purchases count: not our treat, not insurance/protection lines.
    const qualifying = cart.items.filter((i) => !isTreatLine(i) && !isExcluded(i));
    const subtotal = qualifying.reduce((sum, i) => sum + (Number(i.final_line_price) || 0), 0);
    const hasCode = [...enteredCodes(cart)].some((c) => TREAT_CODES.has(c));
    const threshold = thresholdInCents();
    const eligible = hasCode && subtotal >= threshold;
    const wasAdded = attrs[A.added] === '1';
    let declined = attrs[A.declined] === '1';

    // The shopper removed the treat we added while still eligible: respect that,
    // instead of putting it straight back.
    if (eligible && wasAdded && treatLines.length === 0 && !declined) {
      log('treat removed by shopper; marking as declined');
      await write('cart/update.js', { attributes: { [A.declined]: '1', [A.added]: '' } });
      declined = true;
    }

    lastDiagnostics = {
      rate: window.Shopify?.currency?.rate ?? '(not set)',
      thresholdBase: CONFIG.thresholdBase,
      thresholdCents: threshold,
      qualifyingSubtotalCents: subtotal,
      cartTotalCents: cart.total_price,
      enteredCodes: [...enteredCodes(cart)],
      lines: cart.items.map((i) => ({
        title: i.product_title,
        variant_id: i.variant_id,
        final_line_price: i.final_line_price,
        counted: !isTreatLine(i) && !isExcluded(i),
        reason: isTreatLine(i) ? 'free treat' : isExcluded(i) ? 'excluded' : 'counted',
      })),
    };

    lastState = {
      hasCode,
      eligible,
      declined,
      // Is the treat still charged? Then don't tell the shopper it's free.
      // An applied companion code (an ORDER discount worth the treat) covers it
      // even though the treat's own line still shows a price.
      treatCharged: treatLines.some((l) => (Number(l.final_line_price) || 0) > 0) && !companionCovers(cart),
      empty: qualifying.length === 0,
      remaining: Math.max(threshold - subtotal, 0),
      progress: Math.min(subtotal / threshold, 1),   // threshold is always > 0
      currency: cart.currency,
    };
    log('state', lastState);

    const shouldHave = eligible && !declined;
    let changed = false;

    if (shouldHave && treatLines.length === 0) {
      if (treatUnavailable) return false;
      try {
        await write('cart/add.js', {
          items: [{
            id: CONFIG.treatVariantId,
            quantity: 1,
            properties: { [CONFIG.markerProperty]: 'true' },
          }],
        });
      } catch (err) {
        if (err.status === 422) {          // sold out or unavailable: don't retry
          treatUnavailable = true;
          warn('Treat could not be added (sold out or unavailable).');
          return false;
        }
        throw err;
      }
      await write('cart/update.js', { attributes: { [A.added]: '1' } });
      changed = true;
    } else if (treatLines.length > 0) {
      // Should have: exactly one (undo quantity bumps or duplicates). Otherwise none.
      // One update.js call keyed by line key does both, atomically.
      const wanted = shouldHave ? 1 : 0;
      const updates = {};
      let needsChange = false;
      treatLines.forEach((line, idx) => {
        const target = idx === 0 ? wanted : 0;
        updates[line.key] = target;
        if (line.quantity !== target) needsChange = true;
      });
      if (needsChange) {
        const body = { updates };
        // Removed because no longer eligible (not by the shopper): forget we added it,
        // so it can come back if they qualify again.
        if (wanted === 0) body.attributes = { [A.added]: '' };
        await write('cart/update.js', body);
        changed = true;
      }
    }

    // After the treat is sorted, make the companion code match: present while
    // the treat should be in the basket, gone otherwise.
    if (await syncCompanions(cart, shouldHave && !treatUnavailable)) changed = true;

    return changed;
  }

  async function syncFreeTreat() {
    if (syncing) {            // don't run two at once, but don't lose the change either
      pending = true;
      return;
    }
    syncing = true;
    let changed = false;
    try {
      do {
        pending = false;
        changed = (await reconcile()) || changed;
      } while (pending);
      if (changed) refreshCartUI();
    } catch (err) {
      warn(err.message);
    } finally {
      syncing = false;
      renderMessage();
    }
  }

  function scheduleSync() {
    clearTimeout(timer);
    timer = setTimeout(syncFreeTreat, CONFIG.debounceMs);
  }

  // ---- Refreshing the theme's cart -----------------------------------------

  /** Themes render carts differently; adapt this to yours. */
  function refreshCartUI() {
    document.dispatchEvent(new CustomEvent('free-treat:changed'));
    if (/\/cart\/?$/.test(window.location.pathname)) safeReload();
  }

  // Reload the cart page so the theme shows the change, but never more than
  // twice a minute: a reload loop would make the cart unusable.
  function safeReload() {
    try {
      const key = 'free-treat:reloads';
      const now = Date.now();
      const recent = JSON.parse(sessionStorage.getItem(key) || '[]')
        .filter((t) => typeof t === 'number' && now - t < 60000);
      if (recent.length >= 2) {
        warn('Reload suppressed to avoid a loop.');
        return;
      }
      recent.push(now);
      sessionStorage.setItem(key, JSON.stringify(recent));
    } catch {
      return;   // storage blocked: we can't count reloads, so don't risk a loop
    }
    window.location.reload();
  }

  // ---- Progress message ----------------------------------------------------
  //
  // The panel's contents live in a Shadow DOM: a private sub-tree that other
  // apps' scripts (which search the page with querySelector or walk its text)
  // can't see, so they can't rewrite the amount or inject widgets into it.
  //
  // Customising from your theme CSS still works:
  //   • colours: set custom properties on the panel, they pass into the shadow
  //       .free-treat { --ft-ink: #154260; --ft-accent: #ef6915; }
  //   • the panel box itself (margin, padding, background): style .free-treat
  //   • inner parts: .free-treat::part(text | icon | bar | fill | add)
  //   • the font is inherited from the page automatically

  const SHADOW_STYLES = `
:host {
  --ft-ink: #154260;        /* navy: text, outlines */
  --ft-accent: #ef6915;     /* orange: progress fill (the zigzag colour) */
  --ft-highlight: #cee715;  /* lime: the logo star */
  --ft-button: #e0ed46;     /* lime: matches the theme's secondary buttons */
  --ft-panel: #f2f2f2;      /* grey: matches the cart's row background */
  --ft-track: #dee4e8;

  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  column-gap: 1.125rem;
  row-gap: 0.625rem;
  margin: 0 0 1.75rem;
  padding: 1.125rem 1.375rem;
  background: var(--ft-panel);
  color: var(--ft-ink);
  transition: background-color 300ms ease;
}
:host([data-state="unlocked"]) {
  --ft-panel: color-mix(in srgb, var(--ft-highlight) 22%, #fff);
}
:host([hidden]) { display: none; }

.icon { grid-row: span 2; width: 2.75rem; height: 2.75rem; display: block; }
.icon polygon {
  fill: #fff;
  stroke: var(--ft-ink);
  stroke-width: 1.5;
  stroke-linejoin: round;
  transition: fill 300ms ease;
}
:host([data-state="unlocked"]) .icon { animation: pop 600ms cubic-bezier(0.2, 0.9, 0.3, 1.3); }
:host([data-state="unlocked"]) .icon polygon,
:host([data-state="declined"]) .icon polygon,
:host([data-state="pending"]) .icon polygon { fill: var(--ft-highlight); }
:host([data-state="unlocked"]) .icon,
:host([data-state="declined"]) .icon,
:host([data-state="pending"]) .icon { grid-row: auto; }

.text { margin: 0; font-size: 1.125em; line-height: 1.35; }
.amount { font-weight: 700; white-space: nowrap; }
:host([data-state="unlocked"]) .text { font-weight: 700; }

.add {
  font: inherit;
  font-size: 0.8em;
  font-weight: 700;
  color: var(--ft-ink);
  background: var(--ft-button);
  border: 0;
  border-radius: 0;
  padding: 0.35em 0.8em;
  margin-inline-start: 0.6em;
  vertical-align: 0.1em;
  cursor: pointer;
}
.add[hidden] { display: none; }
.add:hover { background: color-mix(in srgb, var(--ft-button) 85%, var(--ft-ink)); }
.add:focus-visible { outline: 2px solid var(--ft-ink); outline-offset: 2px; }
.add:disabled { opacity: 0.6; cursor: progress; }

.bar { height: 0.625rem; background: var(--ft-track); overflow: hidden; }
.fill {
  display: block;
  height: 100%;
  width: var(--ft-progress, 0%);
  background: var(--ft-accent);
  transition: width 500ms cubic-bezier(0.2, 0.7, 0.3, 1);
}
:host([data-state="unlocked"]) .bar,
:host([data-state="declined"]) .bar,
:host([data-state="pending"]) .bar { display: none; }

@keyframes pop {
  0%   { transform: scale(0.6) rotate(-45deg); }
  60%  { transform: scale(1.12) rotate(8deg); }
  100% { transform: none; }
}
@media (max-width: 480px) {
  :host { column-gap: 0.875rem; padding: 1rem; }
  .icon { width: 2.25rem; height: 2.25rem; }
  .text { font-size: 1em; }
}
@media (prefers-reduced-motion: reduce) {
  :host, .fill, .icon polygon { transition: none; }
  :host([data-state="unlocked"]) .icon { animation: none; }
}
@supports not (color: color-mix(in srgb, red, blue)) {
  :host([data-state="unlocked"]) { --ft-panel: #f4f9d3; }
  .add:hover { filter: brightness(0.92); }
}`;

  // Eight-point star, echoing the Shine logo. Decorative, so hidden from screen readers.
  const STAR_SVG =
    '<svg class="icon" part="icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
    '<polygon points="12.00,1.00 14.53,5.90 19.78,4.22 18.10,9.47 23.00,12.00 18.10,14.53 ' +
    '19.78,19.78 14.53,18.10 12.00,23.00 9.47,18.10 4.22,19.78 5.90,14.53 1.00,12.00 ' +
    '5.90,9.47 4.22,4.22 9.47,5.90"/></svg>';

  // Our references into each panel's shadow tree.
  const panels = new WeakMap();

  function formatMoney(cents, currency) {
    try {
      // narrowSymbol shows "$" rather than "US$", matching how themes print prices.
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
      }).format(cents / 100);
    } catch {
      return (cents / 100).toFixed(2);   // unknown currency code: plain number
    }
  }

  function messageState(state) {
    if (!state.eligible) return 'remaining';
    if (state.declined) return 'declined';
    return state.treatCharged ? 'pending' : 'unlocked';
  }

  function buildMessage() {
    // A custom tag, not a <div>: apps that inject into "the first div in the
    // cart form" won't choose it.
    const host = document.createElement('free-treat-banner');
    host.id = 'free-treat-message';
    host.className = 'free-treat';
    host.setAttribute('translate', 'no');   // stop auto-translate tools mangling prices

    const root = host.attachShadow({ mode: 'open' });

    const style = document.createElement('style');
    style.textContent = SHADOW_STYLES;

    // Static, trusted markup only: no cart data goes through innerHTML.
    const iconWrap = document.createElement('span');
    iconWrap.innerHTML = STAR_SVG;
    const icon = iconWrap.firstChild;

    const text = document.createElement('p');
    text.className = 'text';
    text.setAttribute('part', 'text');
    text.setAttribute('role', 'status');     // screen readers announce changes

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'add';
    button.setAttribute('part', 'add');
    button.textContent = CONFIG.messages.addBack;
    button.hidden = true;
    button.addEventListener('click', () => {
      button.disabled = true;
      addTreatBack()
        .catch((err) => warn(err.message))
        .finally(() => { button.disabled = false; });
    });

    const bar = document.createElement('div');
    bar.className = 'bar';
    bar.setAttribute('part', 'bar');
    bar.setAttribute('role', 'progressbar');
    bar.setAttribute('aria-valuemin', '0');
    bar.setAttribute('aria-valuemax', '100');
    bar.setAttribute('aria-label', CONFIG.messages.progressLabel);
    const fill = document.createElement('span');
    fill.className = 'fill';
    fill.setAttribute('part', 'fill');
    bar.append(fill);

    root.append(style, icon, text, bar);
    panels.set(host, { text, button, bar, fill, key: null });
    return host;
  }

  /** Write the text (amount in its own span). Only touches the DOM on change. */
  function setMessageText(refs, state, mode) {
    const template = CONFIG.messages[mode];
    const amount = formatMoney(state.remaining, state.currency);
    const key = `${template}|${amount}`;
    if (refs.key === key) return;
    refs.key = key;

    const { text, button } = refs;
    text.replaceChildren();
    const parts = template.split('{amount}');
    parts.forEach((part, i) => {
      text.append(part);                    // append(string) = text node, never HTML
      if (i < parts.length - 1) {
        const span = document.createElement('span');
        span.className = 'amount';
        span.textContent = amount;
        text.append(span);
      }
    });
    text.append(button);
  }

  const POSITIONS = ['before', 'after', 'prepend', 'append'];

  /** First configured target that exists: { anchor, position }. */
  function findTarget() {
    for (const t of CONFIG.messageTargets) {
      const { selector, position = 'prepend' } = typeof t === 'string' ? { selector: t } : t || {};
      if (!POSITIONS.includes(position)) {
        warn(`Invalid position "${position}" in messageTargets`);
        continue;
      }
      let anchor = null;
      try {
        anchor = document.querySelector(selector);
      } catch {
        warn(`Invalid selector in messageTargets: ${selector}`);
      }
      if (anchor) return { anchor, position };
    }
    return null;
  }

  function isPlaced(el, { anchor, position }) {
    if (position === 'before') return el.nextElementSibling === anchor;
    if (position === 'after') return el.previousElementSibling === anchor;
    return el.parentElement === anchor;
  }

  function place(el, { anchor, position }) {
    if (position === 'before') anchor.before(el);
    else if (position === 'after') anchor.after(el);
    else if (position === 'append') anchor.append(el);
    else anchor.prepend(el);
  }

  // If another app injects its widget INTO our panel, move it out (just above
  // the panel) instead of letting it wreck the layout. Capped, in case that app
  // keeps re-inserting it, so the two scripts can't fight forever.
  let evictions = 0;
  function evictForeignChildren(el) {
    // Our content is all in the shadow tree, so ANY ordinary child is foreign.
    // (It wouldn't even be visible inside the panel, so moving it out matters.)
    for (const child of [...el.children]) {
      if (evictions >= 20) {
        if (evictions === 20) warn('Another script keeps inserting into the panel; leaving it.');
        evictions += 1;
        return;
      }
      evictions += 1;
      el.before(child);
      log('moved a foreign element out of the panel', child);
    }
  }

  /** Show, update or remove the message. Safe to call as often as you like. */
  function renderMessage() {
    let host = document.getElementById('free-treat-message');
    // Something replaced our element with a look-alike: start again.
    if (host && !panels.has(host)) {
      host.remove();
      host = null;
    }

    // No code, empty cart, or nothing to offer: no message, and stop watching the page.
    if (!lastState || !lastState.hasCode || lastState.empty || treatUnavailable) {
      host?.remove();
      setObserving(false);
      return;
    }
    setObserving(true);

    const target = findTarget();
    if (!target) return;

    if (!host || !isPlaced(host, target)) {
      host?.remove();
      host = buildMessage();
      place(host, target);
    }
    evictForeignChildren(host);

    const refs = panels.get(host);
    const mode = messageState(lastState);
    if (host.dataset.state !== mode) host.dataset.state = mode;

    setMessageText(refs, lastState, mode);
    const showButton = mode === 'declined';
    if (refs.button.hidden === showButton) refs.button.hidden = !showButton;

    const pct = String(Math.round(lastState.progress * 100));
    if (refs.bar.getAttribute('aria-valuenow') !== pct) {
      refs.bar.setAttribute('aria-valuenow', pct);
      refs.fill.style.setProperty('--ft-progress', `${pct}%`);
    }
  }

  // Themes re-render the cart by replacing its HTML, which wipes our panel.
  // Watch for that and put it back, but only while there's a panel to show.
  let renderQueued = false;
  let observing = false;
  const observer = new MutationObserver(() => {
    if (renderQueued) return;
    renderQueued = true;
    requestAnimationFrame(() => {
      renderQueued = false;
      renderMessage();
    });
  });
  function setObserving(on) {
    if (on === observing) return;
    observing = on;
    if (on) observer.observe(document.documentElement, { childList: true, subtree: true });
    else observer.disconnect();
  }

  // ---- Public helpers ------------------------------------------------------

  function validCode(code) {
    if (typeof code !== 'string' || !CODE_PATTERN.test(code.trim())) {
      throw new Error('Invalid discount code');
    }
    return code.trim();
  }

  /**
   * Apply a code from a code box, keeping any codes already on the cart.
   * Resolves to { code, status } where status is:
   *   'applied'        Shopify accepted it and it's saving money now
   *   'not_applicable' Shopify kept it, but the basket doesn't qualify yet
   *   'invalid'        Shopify didn't keep it (unknown, expired, not combinable)
   *   'unknown'        this store's cart.js doesn't say
   * Pass { sync: false } if the caller reloads the page itself.
   */
  async function applyCode(code, { sync = true } = {}) {
    const clean = validCode(code);
    const cart = await cartRequest('cart.js');
    const codes = codesOnCart(cart).filter((c) => norm(c) !== norm(clean));
    codes.push(clean);

    const body = { discount: codes.join(',') };
    if (TREAT_CODES.has(norm(clean))) body.attributes = { [A.code]: clean };
    const updated = await cartRequest('cart/update.js', body);

    let status = 'unknown';
    if (Array.isArray(updated.discount_codes)) {
      const entry = updated.discount_codes.find((d) => norm(d?.code) === norm(clean));
      status = !entry ? 'invalid' : entry.applicable ? 'applied' : 'not_applicable';
    }
    if (sync) await syncFreeTreat();
    return { code: clean, status };
  }

  async function removeCode() {
    await cartRequest('cart/update.js', {
      discount: '',
      attributes: { [A.code]: '', [A.declined]: '' },
    });
    await syncFreeTreat();
  }

  /** The shopper changed their mind after removing the treat. */
  async function addTreatBack() {
    await cartRequest('cart/update.js', { attributes: { [A.declined]: '' } });
    await syncFreeTreat();
  }

  // ---- Email link capture --------------------------------------------------
  // Email link: /discount/MoreToLove?redirect=%2F%3Ftreat_code%3DMoreToLove
  // Shopify attaches the code to the cart, then redirects here with ?treat_code=.
  // Only configured treat codes are accepted, and the parameter is removed from
  // the address bar so a refresh or a shared link doesn't carry it.
  function captureCodeFromUrl() {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('treat_code')) return;
    const raw = params.get('treat_code') || '';
    params.delete('treat_code');
    try {
      const query = params.toString();
      history.replaceState(
        history.state,
        '',
        window.location.pathname + (query ? `?${query}` : '') + window.location.hash
      );
    } catch { /* not critical */ }

    let code;
    try {
      code = validCode(raw);
    } catch {
      warn('Ignored malformed treat_code in URL.');
      return;
    }
    if (!TREAT_CODES.has(norm(code))) {
      warn('Ignored unknown treat_code in URL.');
      return;
    }
    cartRequest('cart/update.js', { attributes: { [A.code]: code } })
      .then(scheduleSync)
      .catch((err) => warn(err.message));
  }

  // ---- Triggers ------------------------------------------------------------
  // Watch the theme's own cart calls (theme-agnostic). Our own calls use
  // originalFetch, so they don't trigger this.
  const CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  const urlOf = (input) =>
    typeof input === 'string' ? input : input instanceof URL ? input.href : input?.url || '';

  window.fetch = function patchedFetch(...args) {
    const promise = originalFetch(...args);
    if (CART_MUTATION.test(urlOf(args[0]))) promise.then(scheduleSync, scheduleSync);
    return promise;   // return the original promise: callers see no difference
  };

  // Older themes (jQuery) use XMLHttpRequest instead of fetch.
  const xhrProto = window.XMLHttpRequest?.prototype;
  if (xhrProto) {
    const originalOpen = xhrProto.open;
    const originalSend = xhrProto.send;
    xhrProto.open = function (method, url, ...rest) {
      this.__freeTreatCart = CART_MUTATION.test(String(url));
      return originalOpen.call(this, method, url, ...rest);
    };
    xhrProto.send = function (...args) {
      if (this.__freeTreatCart) this.addEventListener('loadend', scheduleSync, { once: true });
      return originalSend.apply(this, args);
    };
  }

  // Back/forward cache restores a page without reloading it; the cart may have
  // changed in the meantime. Also re-check when the tab comes back into view
  // (the cart may have been changed in another tab).
  window.addEventListener('pageshow', (e) => { if (e.persisted) scheduleSync(); });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') scheduleSync();
  });

  function start() {
    captureCodeFromUrl();
    syncFreeTreat();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  /** Console helper: FreeTreat.diagnose() prints how the amount was worked out. */
  async function diagnose() {
    await syncFreeTreat();
    const d = lastDiagnostics;
    if (!d) return console.info('[free-treat] no data yet');
    console.info('[free-treat] diagnosis', {
      ...d,
      remainingCents: lastState?.remaining,
      messageShown: document.getElementById('free-treat-message')?.shadowRoot?.textContent,
      scriptVersion: '3.2',
    });
    console.table(d.lines);
    return d;
  }

  window.FreeTreat = Object.freeze({
    version: '3.2',
    diagnose,
    sync: syncFreeTreat,
    applyCode,
    removeCode,
    addBack: addTreatBack,
  });
})();
